import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { tokenService } from '../services/tokenService';

const getApiUrl = (): string => {
  // Production - use environment variable or domain
  if (process.env.NODE_ENV === 'production') {
    // Try to use the same protocol as the frontend
    let protocol = 'http://';
    if (typeof window !== 'undefined' && window.location) {
      protocol = window.location.protocol + '//';
    }
    
    // Use environment variable or fallback to domain with correct protocol
    const baseUrl = process.env.REACT_APP_API_URL || 'qbx.co.za';
    
    // If the baseUrl already has a protocol, use it as-is
    if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
      return baseUrl.replace(/\/$/, '');
    }
    
    // Otherwise, add the correct protocol
    return `${protocol}${baseUrl}`.replace(/\/$/, '');
  }
  
  // Development - use local backend
  return (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');
};

const API_URL = getApiUrl();
console.log(`🌐 API URL configured: ${API_URL}`);

// Create axios instance with base URL
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000, // Increased timeout for slower connections
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies/sessions
});

// Request interceptor to add token
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Remove trailing slash from URL if present (except for root)
    if (config.url && config.url.length > 1 && config.url.endsWith('/')) {
      config.url = config.url.slice(0, -1);
      console.log(`📤 Removed trailing slash, new URL: ${config.url}`);
    }
    
    const token = tokenService.getToken();
    
    // Log request details for debugging
    console.log(`📤 ${config.method?.toUpperCase()} ${config.url}`);
    console.log(`📤 Token: ${token ? `Present (${token.substring(0, 20)}...)` : 'Missing'}`);
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('⚠️ No token found for request:', config.url);
    }
    
    return config;
  },
  (error: AxiosError) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`📥 ${response.status} ${response.config.url}`);
    return response;
  },
  (error: AxiosError) => {
    // Log full error for debugging
    console.error('❌ API Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        method: error.config?.method,
      }
    });
    
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      console.warn('⚠️ 401 Unauthorized - Token expired or invalid');
      tokenService.clearToken();
      
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        console.log('🔄 Redirecting to login...');
        window.location.href = '/login';
      }
    }
    
    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.warn('⚠️ 403 Forbidden - Insufficient permissions');
      // Optionally show a message or redirect
    }
    
    // Handle network errors
    if (error.code === 'ERR_NETWORK') {
      console.error('🌐 Network error - Check if backend is running');
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;